import React, { Component, createRef, RefObject } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4pluginsSliceGrouper from '@amcharts/amcharts4/plugins/sliceGrouper';
import _isEqual from 'lodash/isEqual';

interface PerformancePiePerContributionTypeProps {
    chartDivId: string;
    data: PerformanceChartDataElement[];
    dataFields: DataFields;
    height: number;
}

interface PerformancePiePerContributionTypeState {
    chart: am4charts.PieChart | null;
    pieSeries: am4charts.PieSeries | null;
    showLegend: boolean;
}

const percentThreshold = 10;

export class PerformancePiePerContributionType extends Component<
    PerformancePiePerContributionTypeProps,
    PerformancePiePerContributionTypeState
> {
    // There is a race condition in amCharts which can cause issues if we use a chart div ID rather
    // than a ref. The chartDivId prop is only used as a fallback for type safety; the ref
    // should be available by the time componentDidMount is called.
    private divRef: RefObject<HTMLDivElement> | null = null;

    private legendWrapperRef: RefObject<HTMLDivElement> | null = null;

    constructor(props: PerformancePiePerContributionTypeProps) {
        super(props);

        this.clickListener = this.clickListener.bind(this);
        this.divRef = createRef();
        this.legendWrapperRef = createRef();
        this.state = {
            chart: null,
            pieSeries: null,
            showLegend: false,
        };
    }

    componentDidMount(): void {
        document.addEventListener('click', this.clickListener);

        am4core.createDeferred((): am4core.Sprite => {
            const { data, dataFields, chartDivId } = this.props;
            const chart = am4core.create(
                this.divRef?.current ?? chartDivId,
                am4charts.PieChart,
            );
            const pieSeries = chart.series.push(new am4charts.PieSeries());

            chart.legend = new am4charts.Legend();
            chart.legend.position = 'bottom';
            chart.legend.scrollable = true;
            chart.legend.maxHeight = 100;

            chart.data = data;
            chart.numberFormatter.numberFormat = '#.00a';
            chart.numberFormatter.bigNumberPrefixes = [
                { number: 1e3, suffix: 'K' },
                { number: 1e6, suffix: 'M' },
                { number: 1e9, suffix: 'B' },
                { number: 1e12, suffix: 'T' },
                { number: 1e15, suffix: 'Q' },
            ];
            chart.numberFormatter.smallNumberThreshold = 1e-12;
            chart.numberFormatter.smallNumberPrefixes = [
                { number: 1e-12, suffix: '' },
            ];

            chart.innerRadius = am4core.percent(40);

            pieSeries.dataFields.value = dataFields.value;
            pieSeries.dataFields.category = dataFields.category;

            pieSeries.slices.template.stroke = am4core.color('#fff');
            pieSeries.slices.template.strokeWidth = 2;
            pieSeries.slices.template.strokeOpacity = 1;
            pieSeries.slices.template.tooltipText =
                // eslint-disable-next-line no-template-curly-in-string
                '[bold]{category}[/]: ${value.value} ({value.percent}%)';
            pieSeries.alignLabels = false;
            pieSeries.ticks.template.disabled = true;
            pieSeries.labels.template.disabled = true;

            // This creates initial animation
            pieSeries.hiddenState.properties.opacity = 1;
            pieSeries.hiddenState.properties.endAngle = -90;
            pieSeries.hiddenState.properties.startAngle = -90;

            pieSeries.legendSettings.valueText =
                // eslint-disable-next-line no-template-curly-in-string
                '${value.value} ({value.percent}%)';

            const grouper = pieSeries.plugins.push(
                new am4pluginsSliceGrouper.SliceGrouper(),
            );

            grouper.threshold = percentThreshold;
            grouper.groupName = 'Other';
            grouper.clickBehavior = 'zoom';
            grouper.syncLegend = true;

            this.setState({
                chart,
                pieSeries,
            });

            return chart;
        }, this);
    }

    componentDidUpdate(
        prevProps: PerformancePiePerContributionTypeProps,
    ): void {
        const { data } = this.props;
        const { chart } = this.state;

        if (chart !== null) {
            if (!_isEqual(prevProps.data, data)) {
                chart.data = data;
            }
        }
    }

    componentWillUnmount(): void {
        const { chart } = this.state;

        document.removeEventListener('click', this.clickListener);

        if (chart) {
            chart.dispose();
        }
    }

    clickListener(e: Event): void {
        const clickedElement = e.target as HTMLElement;

        if (
            this.legendWrapperRef?.current &&
            e.target &&
            !this.legendWrapperRef.current.contains(clickedElement)
        ) {
            this.setState(
                (
                    prev: PerformancePiePerContributionTypeState,
                ): PerformancePiePerContributionTypeState => {
                    if (prev.showLegend) {
                        return {
                            ...prev,
                            showLegend: false,
                        };
                    }

                    return prev;
                },
            );
        }
    }

    render(): JSX.Element {
        const { chartDivId, height } = this.props;

        return (
            <div className="flex flex-col text-sm">
                <div
                    ref={this.divRef}
                    id={chartDivId}
                    className="w-full"
                    style={{ height }}
                />
            </div>
        );
    }
}
